import Div from '@jumbo/shared/Div';
import { Button, FormHelperText, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { getAddonAvailableQuantity } from 'app/services/apis/ListApi/addonAvailableQty';
import { getAddonsItemNameList } from 'app/services/apis/ListApi/addOnsItemNameList';
import { getFinishedItemSize } from 'app/services/apis/ShopOrderApis/getSizeByFinishItem';
import { Formik, useFormik } from 'formik';
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    addons_available_quantity: Yup.number()
        .min(1, 'Available quantity greater than 0')
        .required('Available quantity is required'),
    addons_quantity: Yup.number()
        .min(1, 'Quantity must be at least 1')
        .required('Quantity is required'),
    dispatch_quantity: Yup.number()
        .min(1, 'Dispatch quantity must be at least 1')
        .required('Dispatch quantity is required'),
    itemName: Yup.string()
        .required('Item name is required'),
    item_size: Yup.string()
        .nullable() // allows null values
        .notRequired(), // makes it optional
});

const AddDispatchAddon = ({ dispatchData, handleAddAddonData }) => {
    const formik = useFormik({
        initialValues: {
            addons_available_quantity: 0,
            addons_quantity: 1,
            addons_status: "no",
            dispatch_quantity: 1,
            itemName: "",
            item_size: null,
            // order_item_addons_id: 815
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const addonData = {
                ...values,
                addons_quantity: values.dispatch_quantity
            }
            handleAddAddonData(addonData)
            formik.handleReset()
        }
    })
    const [itemNameList, setItemNameList] = useState([]);
    const [itemSizeList, setItemSizeList] = useState([]);

    const setAddonQuantity = async function (item_name, item_size) {
        const addonQuantityData = await getAddonAvailableQuantity(item_name, item_size);
        const addonQuantity = addonQuantityData?.[0]?.addon_available_quantity || 0
        formik.setFieldValue('addons_available_quantity', addonQuantity)
    }

    const handleChangeItem = async function (event) {
        const value = event.target.value
        const itemSize = await getFinishedItemSize(value);
        setItemSizeList(itemSize);
        setAddonQuantity(value, formik?.values?.item_size)
        formik.setFieldValue('itemName', value);
        formik.setFieldValue('item_size', null)
    }

    const handleChangeItemSize = async function (event) {
        const value = event.target.value
        setAddonQuantity(formik?.values?.itemName, value)
        formik.setFieldValue('item_size', value)
    }

    useEffect(() => {
        (async function () {
            setItemNameList(await getAddonsItemNameList());
        })()
    }, []);

    return (
        <Div sx={{ mb: 3 }}>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#202020", color: "white" }}>
                            <TableCell
                                sx={{
                                    textAlign: "left",
                                    minWidth: "150px",
                                    verticalAlign: "middle",
                                    color: "white",
                                }}
                            >
                                Item Name
                            </TableCell>
                            <TableCell
                                sx={{
                                    textAlign: "left",
                                    minWidth: "150px",
                                    verticalAlign: "middle",
                                    color: "white",
                                }}
                            >
                                Item Size
                            </TableCell>
                            <TableCell
                                sx={{
                                    textAlign: "left",
                                    minWidth: "120px",
                                    verticalAlign: "middle",
                                    color: "white",
                                }}
                            >
                                Available Quantity
                            </TableCell>
                            <TableCell
                                sx={{
                                    textAlign: "left",
                                    minWidth: "120px",
                                    verticalAlign: "middle",
                                    color: "white",
                                }}
                            >
                                Dispatch Quantity
                            </TableCell>
                            <TableCell
                                sx={{
                                    textAlign: "left",
                                    minWidth: "145px",
                                    verticalAlign: "middle",
                                    color: "white",
                                }}
                            >
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow >
                            <TableCell>
                                <Select
                                    value={formik?.values?.itemName || ""}
                                    name="itemName"
                                    onChange={(e) => handleChangeItem(e)}
                                    size="small"
                                    sx={{ width: "100%" }}
                                    error={Boolean(formik?.errors?.itemName && formik?.touched?.itemName)}
                                >
                                    {itemNameList &&
                                        itemNameList?.length > 0 &&
                                        itemNameList?.map((item) => {
                                            return (
                                                <MenuItem value={item?.addons_name}>
                                                    {item?.addons_name}
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                                {formik?.errors?.itemName && formik?.touched?.itemName && (
                                    <FormHelperText error>{formik.errors.itemName}</FormHelperText>
                                )}
                            </TableCell>
                            <TableCell>
                                <Select
                                    value={formik?.values?.item_size}
                                    name="item_size"
                                    onChange={(e) => handleChangeItemSize(e)}
                                    size="small"
                                    sx={{ width: "100%" }}
                                    error={Boolean(formik?.errors?.item_size && formik?.touched?.item_size)}
                                >
                                    {itemSizeList?.map((item) => {
                                        return <MenuItem value={item}>{item}</MenuItem>;
                                    })}
                                </Select>
                                {formik?.errors?.item_size && formik?.touched?.item_size && (
                                    <FormHelperText error>{formik.errors.item_size}</FormHelperText>
                                )}
                            </TableCell>
                            <TableCell>
                                {formik?.values?.addons_available_quantity}
                                {formik?.errors?.addons_available_quantity && formik?.touched?.addons_available_quantity && (
                                    <FormHelperText error>{formik.errors.addons_available_quantity}</FormHelperText>
                                )}
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    inputProps={{
                                        max: formik?.values?.addons_available_quantity,
                                        min: 1,
                                    }}
                                    onKeyDown={(e) => {
                                        const inputValue = parseInt(e.target.value + e.key, 10);
                                        if (
                                            isNaN(inputValue) ||
                                            inputValue > formik?.values?.addons_available_quantity
                                        ) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onChange={formik?.handleChange}
                                    name="dispatch_quantity"
                                    size="small"
                                    sx={{ width: "100%" }}
                                    value={formik?.values?.dispatch_quantity}
                                    error={Boolean(formik?.errors?.dispatch_quantity && formik?.touched?.dispatch_quantity)}
                                    helperText={formik?.errors?.dispatch_quantity && formik?.touched?.dispatch_quantity ? formik?.errors?.dispatch_quantity : ''}
                                />
                            </TableCell>
                            <TableCell colSpan={9} sx={{ textAlign: "left" }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={formik.handleSubmit}
                                    sx={{ cursor: "pointer" }}
                                >
                                    Add Addon
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Div>
    )
}

export default AddDispatchAddon